import { useEffect, useState, useCallback } from "react";
import { fetchCertificates } from "../../../services/aro/certificateService";
import { fetchEvents } from "../../../services/aro/eventService";
import { CertificateListItem, AROEvent } from "../../../types/aro";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import { formatDateLong, formatLevel } from "../../../utils/formatters";
import "./CertificatesPage.css";

const LIMIT = 50;

interface Props {
  initialEventId?:     string;
  onSelectCertificate: (certificateId: number) => void;
  onSelectPerson:      (personId: number) => void;
}

interface CommittedFilters {
  personName:  string;
  personEmail: string;
  certName:    string;
  eventId:     string;
  level:       string;
}

export default function CertificatesPage({ initialEventId, onSelectCertificate, onSelectPerson }: Props) {
  const [certs,   setCerts]   = useState<CertificateListItem[]>([]);
  const [total,   setTotal]   = useState(0);
  const [offset,  setOffset]  = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  // Input state — UI controls only
  const [personName,  setPersonName]  = useState("");
  const [personEmail, setPersonEmail] = useState("");
  const [certName,    setCertName]    = useState("");
  const [eventId,     setEventId]     = useState(initialEventId ?? "");
  const [level,       setLevel]       = useState("all");
  const [events,      setEvents]      = useState<AROEvent[]>([]);

  // Committed state — what the API uses (only changes on Search / Clear)
  const [committed, setCommitted] = useState<CommittedFilters>({
    personName:  "",
    personEmail: "",
    certName:    "",
    eventId:     initialEventId ?? "",
    level:       "all",
  });

  useEffect(() => {
    fetchEvents().then((r) => setEvents(r.events)).catch(() => {});
  }, []);

  const load = useCallback((off: number) => {
    setLoading(true);
    setError(null);
    fetchCertificates({
      person_name:      committed.personName || undefined,
      person_email:     committed.personEmail || undefined,
      event_id:         committed.eventId || undefined,
      certificate_name: committed.certName || undefined,
      level:            committed.level,
      limit:            LIMIT,
      offset:           off,
    })
      .then((r) => { setCerts(r.certificates); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [committed]);

  useEffect(() => { setOffset(0); load(0); }, [load]);

  const handleSearch = () => {
    setCommitted({ personName, personEmail, certName, eventId, level });
  };

  const clearFilters = () => {
    setPersonName(""); setPersonEmail(""); setCertName(""); setEventId(""); setLevel("all");
    setCommitted({ personName: "", personEmail: "", certName: "", eventId: "", level: "all" });
  };

  const from = total === 0 ? 0 : offset + 1;
  const to   = Math.min(offset + LIMIT, total);

  return (
    <div className="aro-page">
      <PageHeader
        title="Certificates"
        subtitle={committed.eventId ? `Filtered by event · ${total} records` : `${total} total`}
      />

      <div className="aro-filter-bar">
        <input
          className="aro-input"
          placeholder="Name…"
          value={personName}
          onChange={(e) => setPersonName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <input
          className="aro-input"
          placeholder="Email…"
          value={personEmail}
          onChange={(e) => setPersonEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <input
          className="aro-input"
          placeholder="Certificate name…"
          value={certName}
          onChange={(e) => setCertName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <select className="aro-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
          <option value="">All Events</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.event_name}</option>
          ))}
        </select>
        <select className="aro-select" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="all">All Levels</option>
          <option value="1">Level 1</option>
          <option value="2">Level 2</option>
          <option value="3">Level 3</option>
        </select>
        <button className="aro-btn aro-btn--primary" onClick={handleSearch}>Search</button>
        <button className="aro-btn aro-btn--ghost" onClick={clearFilters}>Clear</button>
      </div>

      {error && <div className="aro-error-banner">{error}</div>}

      <div className="aro-card aro-card--no-pad">
        {loading ? (
          <LoadingSpinner />
        ) : certs.length === 0 ? (
          <EmptyState icon="🏅" message="No certificates found." />
        ) : (
          <div className="aro-table-scroll">
            <table className="aro-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Certificate Name</th>
                  <th>Level</th>
                  <th>Credential ID</th>
                  <th>Event Name</th>
                  <th>Earned Date</th>
                </tr>
              </thead>
              <tbody>
                {certs.map((c, i) => (
                  <tr
                    key={c.id}
                    className={[
                      i % 2 === 1 ? "aro-table__row--odd" : "",
                      "aro-table__row--clickable",
                    ].join(" ")}
                    onClick={() => onSelectPerson(c.person_id)}
                  >
                    <td className="aro-table__cell--primary">
                      {c.person_full_name || c.person_email || "—"}
                      {c.person_email && (
                        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-tertiary)", marginTop: 2 }}>
                          {c.person_email}
                        </div>
                      )}
                    </td>
                    <td>{c.certificate_name}</td>
                    <td>{formatLevel(c.level)}</td>
                    <td className="aro-muted">{c.credential_id}</td>
                    <td className="aro-muted">{c.event_name ?? "—"}</td>
                    <td>{formatDateLong(c.certificate_earned_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > LIMIT && (
        <div className="aro-pagination">
          <span className="aro-pagination__info">Showing {from}–{to} of {total}</span>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset === 0}
            onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o); }}
          >
            ← Previous
          </button>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset + LIMIT >= total}
            onClick={() => { const o = offset + LIMIT; setOffset(o); load(o); }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
